import { Typography } from 'antd';
import avatarImg from '@app/assets/images/user-placeholder.png';
import allTeamImage from 'assets/images/all-team.jpg';
import React, { useCallback, useEffect, useState } from 'react';
import * as S from './DealRoomTeams.styles';
import { IUser } from '@app/interfaces/user';
import { getAWSSignedURL } from '@app/api/file.api';
import { getTeam } from '@app/api/users.api';
import { notificationController } from '@app/controllers/notificationController';
import { UserTeamLevel } from '@app/constants/enums/user';
import { getUserFullName } from '@app/utils/utils';

const { Text } = Typography;

const UserAvatar: React.FC<{ member: IUser }> = ({ member }) => {
  const [imageUrl, setImageUrl] = useState<string>(avatarImg);

  useEffect(() => {
    async function fetchImage(image: string) {
      try {
        const signedUrl = await getAWSSignedURL(image);
        if (signedUrl) setImageUrl(signedUrl);
      } catch (error) {}
    }

    if (member?.image) {
      fetchImage(member?.image as unknown as string);
    }
  }, [member?.image]);

  return <S.Avatar src={imageUrl} alt={member?.firstname} />;
};

const DealRoomTeams: React.FC = () => {
  const [team, setTeam] = useState<IUser[]>([]);
  const [rowConfig, setRowConfig] = useState<{ columns: number; members: IUser[] }[]>([]);

  const fetchTeam = useCallback(async () => {
    try {
      const data = await getTeam();
      setTeam(data);
    } catch (error) {
      notificationController.error({ message: (error as Error).message });
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    if (team.length > 0) {
      const totalTeam = team.length + 100;
      const calculateRowConfig = (total: number, perRow: number, users: IUser[]) => {
        const result = [];
        const sortedUsers = users.sort((a, b) => (a.teamOrder ?? totalTeam) - (b.teamOrder ?? totalTeam));
        if (total > perRow) {
          const totalRows = Math.ceil(total / perRow);
          for (let i = 0; i < totalRows; i++) {
            const count = i === totalRows - 1 ? total - i * perRow : perRow;
            result.push({ columns: perRow, members: sortedUsers.slice(i * perRow, i * perRow + count) });
          }
        } else if (total > 0) {
          result.push({ columns: perRow, members: sortedUsers.slice(0, total) });
        }
        return result;
      };

      const config = [];
      const levelOne = team.filter((m) => m.teamLevel === UserTeamLevel.LEVEL_1);
      config.push(...calculateRowConfig(levelOne.length, 4, levelOne));
      const levelTwo = team.filter((m) => m.teamLevel === UserTeamLevel.LEVEL_2);
      config.push(...calculateRowConfig(levelTwo.length, 5, levelTwo));
      const levelThree = team.filter((m) => m.teamLevel === UserTeamLevel.LEVEL_3);
      config.push(...calculateRowConfig(levelThree.length, 6, levelThree));
      setRowConfig(config);
    } else {
      setRowConfig([]);
    }
  }, [team]);

  return (
    <S.Wrapper>
      {rowConfig.map(({ columns, members }, rowIndex) => {
        return (
          <S.TeamListWrapper className={`team-list-wrapper-${columns}`} key={rowIndex} columns={columns}>
            {members.map((member) => (
              <S.TeamCard className="team-card" key={member.id} hoverable cover={<UserAvatar member={member} />}>
                <S.CardContent>
                  <Text strong className="name">
                    {getUserFullName(member)}
                  </Text>
                  {member.title && (
                    <Text type="secondary" className="role">
                      {member.title}
                    </Text>
                  )}
                </S.CardContent>
              </S.TeamCard>
            ))}
          </S.TeamListWrapper>
        );
      })}

      <S.AllTeamImage src={allTeamImage} />
    </S.Wrapper>
  );
};

export default DealRoomTeams;
